import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, Minus } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionDivider from "../components/SectionDivider";
import PricingRequestModal from "../components/PricingRequestModal";
import SEO from "../components/SEO";

const tiers = [
  {
    name: "Standard",
    setup: 1500,
    monthly: 199,
    blurb: "Everything you need to launch your first automation.",
    features: [
      "1 automation workflow",
      "Document Triage System",
      "Email notification alerts",
      "Notion dashboard setup",
      "Loom walkthrough video",
      "Email support",
    ],
    highlighted: false,
  },
  {
    name: "Advanced",
    setup: 2000,
    monthly: 199,
    blurb: "Everything in Standard, plus deeper automation.",
    features: [
      "Up to 3 automation workflows",
      "Lead qualification system",
      "Automated quote generation",
      "Priority support",
      "Everything in Standard",
    ],
    highlighted: true,
    badge: "Most Popular",
  },
  {
    name: "Enterprise",
    setup: 3000,
    monthly: 199,
    blurb: "For teams running serious operations end-to-end.",
    features: [
      "4+ automation workflows",
      "Custom routing logic",
      "Multi-inbox support",
      "Dedicated build session",
      "Phone support",
      "Everything in Advanced",
    ],
    highlighted: false,
  },
];

const faqs = [
  { q: "How long does setup take?", a: "Most automation systems are built and delivered within 48 hours of receiving your access credentials." },
  { q: "Do I need to change any of my existing tools or software?", a: "No. We connect to the tools you already use — Gmail, Google Drive, spreadsheets, and more." },
  { q: "What happens if something breaks?", a: "Your monthly maintenance subscription covers monitoring and fixes. We'll notify you of any issues and resolve them promptly." },
  { q: "Is there a contract or lock-in period?", a: "No lock-in contracts. Your monthly subscription can be cancelled at any time with written notice." },
  { q: "What do you need from me to get started?", a: "Just access to your email inbox and a few sample documents. We handle everything else." },
  { q: "Can I customize what data gets extracted?", a: "Absolutely. Every build is custom. We extract exactly the fields that matter to your business." },
];

const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const monthlyPrice = (m) => (annual ? Math.round(m * 12 * 0.9) : m);
  const monthlyLabel = annual ? "/year" : "/month";

  const openModal = (tier) => {
    setSelectedPlan({
      name: tier.name,
      setup: tier.setup,
      monthly: monthlyPrice(tier.monthly),
      label: monthlyLabel,
    });
    setModalOpen(true);
  };

  return (
    <div data-testid="pricing-page">
      <SEO
        title="Pricing — Zelaflo Labs Automation Packages"
        description="Simple, transparent pricing. One setup fee, one monthly rate. Choose the workflow automation package that fits your business."
        path="/pricing"
      />
      <PricingRequestModal open={modalOpen} onClose={() => setModalOpen(false)} plan={selectedPlan} />
      <section className="relative pt-40 pb-16 overflow-hidden" data-testid="pricing-hero">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 z-10 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Pricing</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter max-w-4xl mx-auto text-balance" style={{ color: "var(--text)" }}>
              Simple, Transparent <span className="text-[#7B2FBE]">Pricing.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg md:text-xl max-w-2xl mx-auto" style={{ color: "var(--text-muted)" }}>
              One setup fee. One monthly rate. No surprises.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10 inline-flex items-center gap-3 p-1.5 rounded-full border" style={{ backgroundColor: "var(--card-tint)", borderColor: "var(--border-strong)" }} data-testid="pricing-toggle">
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!annual ? "btn-primary-zl text-white" : ""}`}
                style={!annual ? {} : { color: "var(--text-muted)" }}
                data-testid="toggle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${annual ? "btn-primary-zl text-white" : ""}`}
                style={annual ? {} : { color: "var(--text-muted)" }}
                data-testid="toggle-annual"
              >
                Annual <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/20 text-white">-10%</span>
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-16 md:py-20" data-testid="pricing-cards-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {tiers.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div
                  className={`gradient-border ${t.highlighted ? "gradient-border-strong md:scale-[1.04]" : ""} relative p-8 md:p-10 rounded-3xl h-full flex flex-col transition-all duration-500 hover:-translate-y-2`}
                  style={{ backgroundColor: "var(--surface)" }}
                  data-testid={`pricing-card-${t.name.toLowerCase()}`}
                  data-pricing-card={t.name.toLowerCase()}
                >
                  {t.badge && (
                    <span
                      className="zl-pill absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full"
                      data-testid={`pricing-badge-${t.name.toLowerCase()}`}
                    >
                      {t.badge}
                    </span>
                  )}
                  <h3 className="font-heading text-2xl font-semibold tracking-tight" style={{ color: "var(--text)" }}>{t.name}</h3>
                  <p className="mt-2 text-sm" data-pricing-blurb style={{ color: "var(--text-muted)" }}>{t.blurb}</p>

                  <div className="mt-8">
                    <div className="flex items-baseline gap-2">
                      <span className="font-heading text-5xl md:text-6xl font-bold tracking-tighter" data-pricing-price style={{ color: "var(--text)" }}>${t.setup.toLocaleString()}</span>
                      <span className="text-sm" data-pricing-meta style={{ color: "var(--text-subtle)" }}>setup</span>
                    </div>
                    <div className="mt-3 text-sm" data-pricing-monthly style={{ color: "var(--text-muted)" }}>
                      <span className="font-semibold text-lg" style={{ color: "var(--text)" }}>${monthlyPrice(t.monthly).toLocaleString()}</span>
                      <span style={{ color: "var(--text-muted)" }}>{monthlyLabel}</span>
                      <span style={{ color: "var(--text-subtle)" }}> after 30-day free trial</span>
                    </div>
                  </div>

                  <ul className="mt-8 space-y-3 flex-1">
                    {t.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm leading-relaxed" data-pricing-feature style={{ color: "var(--text-muted)" }}>
                        <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-[#7B2FBE] zl-on-purple">
                          <Check size={12} className="text-white" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => openModal(t)}
                    className="btn-primary-zl mt-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300"
                    data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  >
                    Get Started
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* FAQ */}
      <section className="relative py-24 md:py-32" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3 text-center">FAQ</p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-center" style={{ color: "var(--text)" }}>Questions, answered.</h2>
          </Reveal>
          <div className="mt-12 divide-y border-y" style={{ borderColor: "var(--border)" }}>
            {faqs.map((f, i) => {
              const open = openIdx === i;
              return (
                <Reveal key={i} delay={i * 0.04}>
                  <button
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full text-left py-6 flex items-center justify-between gap-6 group"
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={open}
                  >
                    <span className="font-heading text-lg md:text-xl font-medium group-hover:text-[#7B2FBE] transition-colors" style={{ color: "var(--text)" }}>{f.q}</span>
                    <motion.span
                      animate={{ rotate: open ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${open ? "bg-[#7B2FBE] text-white zl-on-purple" : ""}`}
                      style={open ? {} : { backgroundColor: "var(--card-tint)", color: "var(--text-muted)" }}
                    >
                      {open ? <Minus size={16} /> : <Plus size={16} />}
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                        data-testid={`faq-answer-${i}`}
                      >
                        <p className="pb-6 leading-relaxed" style={{ color: "var(--text-muted)" }}>{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
